'use client'

import { Builder } from '@builder.io/react'

import { SplitContent } from './SplitContent'
import { defaults } from './SplitContent.defaults'

export const ContentSides = ['left', 'right']
export const ContentSizes = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]
export const ImageSizes = ['cover', 'contain']
export const ImagePositions = ['center', 'top', 'bottom', 'left', 'right']
export const ImageTransformOrigins = ['center', 'top', 'bottom']
export const MediaTypes = ['photo', 'video', 'blocks']
export const PaddingYVariants = ['large', 'medium'] as const

Builder.registerComponent(SplitContent, {
  name: 'SplitContent',
  noWrap: true,
  inputs: [
    {
      name: 'children',
      type: 'blocks',
      hideFromUI: true,
      helperText: 'This is an editable region.',
      defaultValue: defaults.children,
    },
    {
      name: 'contentSide',
      type: 'string',
      enum: ContentSides,
      defaultValue: ContentSides[0],
      helperText: 'The side of the text content. The photo will be on the opposite side.',
    },
    {
      name: 'roundedNone',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Unrounded edges',
    },
    {
      name: 'mobileRoundedNone',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Unrounded edges on mobile',
    },
    {
      name: 'mobileFlip',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Flip the content and photo on mobile.',
    },
    {
      name: 'paddingYVariant',
      friendlyName: 'padding Y',
      type: 'string',
      enum: PaddingYVariants.map(String),
      defaultValue: 'large',
      helperText: 'Top and bottom content padding',
    },
    {
      name: 'contentSize',
      type: 'string',
      enum: ContentSizes.map(String),
      defaultValue: String(ContentSizes[5]),
      helperText: 'The width of the text content. The photo will have the remaining space.',
    },
    {
      name: 'backgroundColor',
      type: 'color',
      defaultValue: 'rgba(22, 24, 25, 1)',
      helperText: 'The background color of the whole component.',
    },
    {
      name: 'backgroundImageUrl',
      type: 'file',
      helperText:
        'The background image of the whole component. To add image to one side, use the "Photo url" option.',
    },
    {
      name: 'showBackgroundOptions',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Show options to customize the background image alignment and fitment.',
    },
    {
      name: 'backgroundSize',
      type: 'string',
      enum: ImageSizes,
      defaultValue: ImageSizes[0],
      showIf: (properties) => properties.get('showBackgroundOptions'),
    },
    {
      name: 'backgroundPosition',
      type: 'string',
      enum: ImagePositions,
      defaultValue: ImagePositions[0],
      showIf: (properties) => properties.get('showBackgroundOptions'),
    },
    {
      name: 'mediaType',
      type: 'string',
      enum: MediaTypes,
      defaultValue: MediaTypes[0],
      helperText: 'Photo, video or builder blocks.',
    },
    {
      name: 'brightcoveVideoId',
      type: 'string',
      showIf: (properties) => properties.get('mediaType') === 'video',
    },
    {
      name: 'playAsPopup',
      type: 'boolean',
      showIf: (properties) => properties.get('mediaType') === 'video',
    },
    {
      name: 'photoUrl',
      type: 'file',
      showIf: (properties) => properties.get('mediaType') === 'photo',
      defaultValue: defaults.photoUrl,
    },
    {
      name: 'showPhotoOptions',
      type: 'boolean',
      defaultValue: false,
      showIf: (properties) => properties.get('mediaType') === 'photo',
      helperText: 'Show options to customize the background image alignment and fitment.',
    },
    {
      name: 'mediaBlocks',
      type: 'blocks',
      hideFromUI: true,
      helperText: 'This is an editable region.',
      showIf: (properties) => properties.get('mediaType') === 'blocks',
      defaultValue: defaults.children,
    },
    {
      name: 'photoSize',
      type: 'string',
      enum: ImageSizes,
      defaultValue: ImageSizes[0],
      showIf: (properties) => properties.get('showPhotoOptions'),
    },
    {
      name: 'photoPosition',
      type: 'string',
      enum: ImagePositions,
      defaultValue: ImagePositions[0],
      showIf: (properties) => properties.get('showPhotoOptions'),
    },
    {
      name: 'photoScale',
      type: 'number',
      defaultValue: 1,
      showIf: (properties) => properties.get('showPhotoOptions'),
      helperText: 'Scale the photo to create an overflow effect.',
    },
    {
      name: 'photoTransformOriginY',
      type: 'string',
      enum: ImageTransformOrigins,
      defaultValue: 'center',
      showIf: (properties) => properties.get('showPhotoOptions'),
      helperText: 'Photo y-axis anchor point.',
    },
    {
      name: 'showMobileBackgroundOptions',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'mobileBackgroundHidden',
      type: 'boolean',
      defaultValue: false,
      showIf: (properties) => properties.get('showMobileBackgroundOptions'),
    },
    {
      name: 'mobileBackgroundColor',
      type: 'color',
      showIf: (properties) => properties.get('showMobileBackgroundOptions'),
    },
    {
      name: 'mobileBackgroundImageUrl',
      type: 'file',
      showIf: (properties) => properties.get('showMobileBackgroundOptions'),
    },
    {
      name: 'mobileBackgroundSize',
      type: 'string',
      enum: ImageSizes,
      showIf: (properties) => properties.get('showMobileBackgroundOptions'),
    },
    {
      name: 'mobileBackgroundPosition',
      type: 'string',
      enum: ImagePositions,
      showIf: (properties) => properties.get('showMobileBackgroundOptions'),
    },
    {
      name: 'showMobilePhotoOptions',
      type: 'boolean',
      defaultValue: false,
      helperText: 'These options will only be used on mobile and override the desktop options.',
    },
    {
      name: 'mobilePhotoHidden',
      type: 'boolean',
      defaultValue: false,
      helperText: 'Hide the photo section on mobile.',
      showIf: (properties) => properties.get('showMobilePhotoOptions'),
    },
    {
      name: 'mobilePhotoUrl',
      type: 'file',
      showIf: (properties) => properties.get('showMobilePhotoOptions'),
    },
    {
      name: 'mobilePhotoSize',
      type: 'string',
      enum: ImageSizes,
      showIf: (properties) => properties.get('showMobilePhotoOptions'),
    },
    {
      name: 'mobilePhotoPosition',
      type: 'string',
      enum: ImagePositions,
      showIf: (properties) => properties.get('showMobilePhotoOptions'),
    },
    {
      name: 'mobilePhotoTransformOriginY',
      type: 'string',
      enum: ImageTransformOrigins,
      defaultValue: 'center',
      showIf: (properties) => properties.get('showMobilePhotoOptions'),
      helperText: 'Photo y-axis anchor point.',
    },
    {
      name: 'mobilePhotoScale',
      type: 'number',
      showIf: (properties) => properties.get('showMobilePhotoOptions'),
    },
    {
      name: 'mobilePhotoHeight',
      type: 'number',
      defaultValue: 300,
      helperText: 'The height of the photo section on mobile.',
      showIf: (properties) => properties.get('showMobilePhotoOptions'),
    },
    {
      name: 'videoSEO',
      type: 'object',
      friendlyName: 'Video SEO data',
      helperText:
        'VideoObject schema markup for SEO. If this is filled out, it will output the JSON-LD.',
      showIf: (properties) => properties.get('mediaType') === 'video',
      subFields: [
        {
          name: 'name',
          type: 'text',
          friendlyName: 'SEO name',
        },
        {
          name: 'description',
          type: 'string',
          friendlyName: 'SEO description',
        },
        {
          name: 'thumbnailUrl',
          type: 'string',
          friendlyName: 'SEO thumbnail URL',
        },
        {
          name: 'uploadDate',
          type: 'string',
          friendlyName: 'SEO upload date',
        },
        {
          name: 'videoDuration',
          type: 'string',
          friendlyName: 'SEO video duration',
        },
        {
          name: 'embedUrl',
          type: 'string',
          friendlyName: 'SEO embed URL',
        },
      ],
    },
  ],
  image: 'https://unpkg.com/@tabler/icons@2.44.0/icons/columns-2.svg',
})
